import { triggerToast } from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { selectCompany } from "../../homeSlice";
import {
  cardsSlice,
  cardsListAsync,
  getQrCodePresignedUrlAsync,
  selectCardsListStatus,
  selectOrderMultipleCardsStatus,
  updateCardStatusAsync,
  selectGetAddressStatus,
  addressAsync,
} from "./cardsSlice";
import { CardsListType, QrCodeInfo } from "./cardsAPI";
import { useNavigate, useParams } from "react-router-dom";
import { CardSidePanel } from "./sidePanel/CardSidePanel";
import "../../../../i18n/i18n";
import { SharedInterfaceCards } from "./sharedInterface";
import { rulesListAsync, selectRulesListStatus } from "../../rules/rulesSlice";
import {
  listAllVehiclesInDbAsync,
  selectListAllVehiclesInDbStatus,
} from "../../vehicles/vehiclesSlice";

export const Cards = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const company = useAppSelector(selectCompany);
  const orderMultipleCardStatus = useAppSelector(
    selectOrderMultipleCardsStatus,
  );
  const cardsListStatus = useAppSelector(selectCardsListStatus);
  const listAllVehiclesInDbStatus = useAppSelector(
    selectListAllVehiclesInDbStatus,
  );
  const rulesListStatus = useAppSelector(selectRulesListStatus);
  const getAddressStatus = useAppSelector(selectGetAddressStatus);

  useEffect(() => {
    if (company && cardsListStatus === "idle") {
      void dispatch(cardsListAsync(company.uuid));
    }
  }, [dispatch, cardsListStatus, company]);

  useEffect(() => {
    if (company && listAllVehiclesInDbStatus === "idle") {
      void dispatch(listAllVehiclesInDbAsync({ companyUuid: company.uuid }));
    }
  }, [dispatch, company, listAllVehiclesInDbStatus]);

  useEffect(() => {
    if (company && rulesListStatus === "idle") {
      void dispatch(rulesListAsync(company.uuid));
    }
  }, [dispatch, rulesListStatus, company]);

  useEffect(() => {
    if (company && getAddressStatus === "idle") {
      void dispatch(addressAsync(company.uuid));
    }
  }, [dispatch, getAddressStatus, company]);

  useEffect(() => {
    if (orderMultipleCardStatus === "success") {
      triggerToast(t("cards.multipleCards.creation.success") || "", "valid");
      dispatch(cardsSlice.actions.resetOrderMultipleCardsStatus());
    } else if (orderMultipleCardStatus === "failed") {
      triggerToast(t("cards.multipleCards.creation.failure") || "", "error");
      dispatch(cardsSlice.actions.resetOrderMultipleCardsStatus());
    }
  }, [dispatch, orderMultipleCardStatus, t]);

  const sidePanelAction = useCallback(
    (props: Partial<CardsListType>) => {
      navigate(`/home/cards/cards/${props.uuid}`);
    },
    [navigate],
  );

  const lockCardAction = useCallback(
    (isCardLocked: boolean, cardUuid: string) => {
      void dispatch(
        updateCardStatusAsync({
          cardUuid,
          status: isCardLocked ? "LOCKED" : "UNLOCKED",
        }),
      );
    },
    [dispatch],
  );

  const getQrCodePresignedUrlAction = useCallback(
    (qrCode: QrCodeInfo) => {
      void dispatch(getQrCodePresignedUrlAsync(qrCode));
    },
    [dispatch],
  );

  if (params.tab !== "cards") {
    return;
  }

  return (
    <SharedInterfaceCards
      sidePanelAction={sidePanelAction}
      sidePanel={
        <CardSidePanel
          canRedirectToRule
          canDeactivate
          canUpdateRule
          lockCardAction={lockCardAction}
          getQrCodePresignedUrlAction={getQrCodePresignedUrlAction}
        />
      }
    />
  );
};
